import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../services/global-environment';

/**
 * 地點資料介面
 */
export interface FloorplanLocation {
  id: string;
  companyid: string;
  created_by: string;
  location_name: string;
  location_code?: string;
  description?: string;
  building_name?: string;
  floor_level?: string;
  department?: string;
  responsible_person?: string;
  location_type?: string;
  hazard_level?: string;
  special_requirements?: string;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * API 回應格式
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  count?: number;
  message?: string;
  error?: any;
}

/**
 * 地點資訊管理服務
 */
@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private apiUrl = environment.apiUrl;
  private companyId = environment.companyid;

  constructor(private http: HttpClient) {}

  /**
   * 建立新地點
   */
  createLocation(locationData: Omit<FloorplanLocation, 'id' | 'companyid' | 'createdAt' | 'updatedAt'>): Observable<ApiResponse<FloorplanLocation>> {
    const payload = {
      ...locationData,
      companyid: this.companyId,
      created_by: locationData.created_by || 'current-user'
    };

    return this.http.post<ApiResponse<FloorplanLocation>>(
      `${this.apiUrl}/api/location/create`,
      payload
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * 查詢地點列表
   */
  getLocationList(activeOnly: boolean = true): Observable<ApiResponse<FloorplanLocation[]>> {
    let params = new HttpParams()
      .set('companyid', this.companyId);
    
    if (activeOnly) {
      params = params.set('active_only', 'true');
    }

    return this.http.get<ApiResponse<FloorplanLocation[]>>(
      `${this.apiUrl}/api/location/list`,
      { params }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * 取得單一地點詳細資訊
   */
  getLocationById(locationId: string): Observable<ApiResponse<FloorplanLocation>> {
    const params = new HttpParams().set('companyid', this.companyId);

    return this.http.get<ApiResponse<FloorplanLocation>>(
      `${this.apiUrl}/api/location/${locationId}`,
      { params }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * 更新地點資訊
   */
  updateLocation(locationId: string, locationData: Partial<FloorplanLocation>): Observable<ApiResponse<FloorplanLocation>> {
    const payload = {
      ...locationData,
      companyid: this.companyId
    };

    return this.http.put<ApiResponse<FloorplanLocation>>(
      `${this.apiUrl}/api/location/${locationId}`,
      payload
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * 刪除地點（軟刪除）
   */
  deleteLocation(locationId: string): Observable<ApiResponse<FloorplanLocation>> {
    const params = new HttpParams().set('companyid', this.companyId);

    return this.http.delete<ApiResponse<FloorplanLocation>>(
      `${this.apiUrl}/api/location/${locationId}`,
      { params }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * 搜尋地點
   */
  searchLocations(keyword?: string, limit: number = 20): Observable<ApiResponse<FloorplanLocation[]>> {
    let params = new HttpParams()
      .set('companyid', this.companyId)
      .set('limit', limit.toString());

    if (keyword && keyword.trim()) {
      params = params.set('keyword', keyword.trim());
    }

    return this.http.get<ApiResponse<FloorplanLocation[]>>(
      `${this.apiUrl}/api/location/search/locations`,
      { params }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * 錯誤處理
   */
  private handleError(error: any): Observable<never> {
    console.error('Location service error:', error);
    
    let errorMessage = '操作失敗';
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return throwError(() => new Error(errorMessage));
  }
}